import React, { useCallback, useState } from 'react';

import { TileGridBlockData } from 'api/cms';
import { Box } from 'components/Box';
import { H2 } from 'components/Text';
import { colors } from 'theme';
import { useOnWindowKeyDown } from 'utils/hooks';

import { TileGridBlockTile } from './Tile';
import { SectionButton } from './Tile/TileGridBlock.styled';

type Props = {
  blockIndex: number;
  data: TileGridBlockData;
};

export function TileGridBlock({
  blockIndex,
  data: { sections, imageSize },
}: Props) {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const currentSection = sections[currentSectionIndex];

  const [currentTileIndex, setCurrentTileIndex] = useState<number>();
  const closeTile = useCallback(() => setCurrentTileIndex(undefined), []);

  const hasPrevious = currentTileIndex !== undefined && currentTileIndex > 0;
  const hasNext =
    currentTileIndex !== undefined &&
    currentTileIndex < currentSection.tiles.length - 1;

  const previous = useCallback(() => {
    setCurrentTileIndex((index) => (index || 0) - 1);
  }, []);

  const next = useCallback(() => {
    setCurrentTileIndex((index) => (index || 0) + 1);
  }, []);

  useOnWindowKeyDown(['Escape'], closeTile);

  return (
    <Box data-qa-id="cms-tile-grid">
      {sections.length > 1 && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" mb={48}>
          {sections.map(({ name }, index) => (
            <SectionButton
              data-qa-id={`cms-tg-${name}`}
              key={name}
              px={24}
              my={16}
              borderLeft={index > 0 ? `1px solid ${colors.aluminum}` : '0'}
              $active={index === currentSectionIndex}
              onClick={() => setCurrentSectionIndex(index)}
            >
              <H2 my={0} styleAs="body">
                {name}
              </H2>
            </SectionButton>
          ))}
        </Box>
      )}

      <Box display="flex" flexWrap="wrap" justifyContent="center">
        {currentSection.tiles.map((tile, index) => (
          <Box
            key={tile.heading}
            display="flex"
            justifyContent="center"
            width={[1, 1 / 2, 1 / 4]}
            maxWidth={400}
            flex="0 0 auto"
          >
            <TileGridBlockTile
              tile={tile}
              isOpen={index === currentTileIndex}
              onOpen={() => setCurrentTileIndex(index)}
              onClose={closeTile}
              onNext={hasNext ? next : undefined}
              onPrevious={hasPrevious ? previous : undefined}
              imageSize={imageSize}
              blockIndex={blockIndex}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
